import type { Car } from "../physics/car";
import type { Track } from "../track/track";
import {
  mat4Create,
  mat4Perspective,
  mat4LookAt,
  mat4Multiply,
  mat4FromTranslation,
  mat4FromRotationY,
  mat4FromRotationZ,
  mat4FromScaling,
  mat4Ortho,
  type Mat4,
} from "./math";
import vertSrc from "./shaders/main.vert";
import fragSrc from "./shaders/main.frag";
import skyVertSrc from "./shaders/sky.vert";
import skyFragSrc from "./shaders/sky.frag";
import shadowVertSrc from "./shaders/shadow.vert";
import shadowFragSrc from "./shaders/shadow.frag";
import bloomExtractFragSrc from "./shaders/bloom_extract.frag";
import bloomBlurFragSrc from "./shaders/bloom_blur.frag";
import compositeFragSrc from "./shaders/composite.frag";

// --- Types ---

interface StaticMesh {
  vao: WebGLVertexArrayObject;
  count: number;
  mode: number;
}

interface PostProcess {
  // Scene render target (full-res, colour + depth)
  sceneFbo: WebGLFramebuffer;
  sceneTex: WebGLTexture;
  sceneDepth: WebGLRenderbuffer;
  // Bloom ping-pong FBOs (half-res, no depth)
  bloomFboA: WebGLFramebuffer;
  bloomTexA: WebGLTexture;
  bloomFboB: WebGLFramebuffer;
  bloomTexB: WebGLTexture;
  // Fullscreen quad VAO (reuses sky quad geometry)
  quad: WebGLVertexArrayObject;
  // Programs + uniform locations
  extractProg: WebGLProgram;
  uExtractScene: WebGLUniformLocation;
  blurProg: WebGLProgram;
  uBlurInput: WebGLUniformLocation;
  uBlurTexel: WebGLUniformLocation;
  uBlurHoriz: WebGLUniformLocation;
  compositeProg: WebGLProgram;
  uCompScene: WebGLUniformLocation;
  uCompBloom: WebGLUniformLocation;
  uCompBloomStr: WebGLUniformLocation;
  uCompMotionBlur: WebGLUniformLocation;
  // Dimensions
  w: number;
  h: number;
}

export type CameraMode = "cockpit" | "chase" | "chase-north";

interface CameraState {
  x: number;
  y: number;
  z: number;
  tx: number;
  ty: number;
  tz: number;
  mode: CameraMode;
}

export interface Renderer {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  program: WebGLProgram;
  u: {
    model: WebGLUniformLocation;
    view: WebGLUniformLocation;
    projection: WebGLUniformLocation;
    color: WebGLUniformLocation;
    lightDir: WebGLUniformLocation;
    ambient: WebGLUniformLocation;
    roughness: WebGLUniformLocation;
    metallic: WebGLUniformLocation;
    cameraPos: WebGLUniformLocation;
    shadowMatrix: WebGLUniformLocation;
    shadowMap: WebGLUniformLocation;
  };
  shadow: {
    program: WebGLProgram;
    uModel: WebGLUniformLocation;
    uLightVP: WebGLUniformLocation;
    fbo: WebGLFramebuffer;
    depthTex: WebGLTexture;
    size: number;
  };
  ground: StaticMesh;
  gravel: StaticMesh;
  surface: StaticMesh;
  curbL: StaticMesh;
  curbR: StaticMesh;
  edgeL: StaticMesh;
  edgeR: StaticMesh;
  startFinishWhite: StaticMesh;
  startFinishBlack: StaticMesh;
  centerDashes: StaticMesh;
  box: StaticMesh;
  cylinder: StaticMesh;
  skyProgram: WebGLProgram;
  skyQuad: StaticMesh;
  cam: CameraState;
  // Post-processing is lazily initialised AFTER the first frame paints (see
  // ensurePostProcess) so its 3 shader compiles + FBO/texture allocations stay
  // off the path to first paint — they were delaying FCP/LCP/Speed Index. Null
  // until built.
  pp: PostProcess | null;
  // Capability gate decided ONCE at init: false on software renderers
  // (SwiftShader/llvmpipe/etc. — no real GPU), where the multi-pass
  // post-process pipeline is unaffordable and each frame would become a
  // multi-hundred-ms long task. When false, post-processing is never built or
  // enabled, so the scene renders straight to the canvas exactly like the
  // pre-bloom baseline. This is the primary recovery for the web-health probe,
  // which runs headless Chrome on SwiftShader.
  allowPostProcess: boolean;
  // Runtime toggle within an allowing device: the frame-time governor may still
  // shed post-processing if a real-but-slow GPU can't sustain it. Always false
  // when allowPostProcess is false.
  postProcessEnabled: boolean;
}

/** True for software/CPU GL renderers (no real GPU) where multi-pass
 *  post-processing is unaffordable. Detected from the unmasked renderer string. */
function isSoftwareRenderer(gl: WebGL2RenderingContext): boolean {
  const dbg = gl.getExtension("WEBGL_debug_renderer_info");
  const name = dbg
    ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL))
    : "";
  return /swiftshader|llvmpipe|software|basic render|microsoft|mesa offscreen/i.test(name);
}

// --- Init ---

export function initWebGL(
  canvas: HTMLCanvasElement,
  track: Track
): Renderer {
  const gl = canvas.getContext("webgl2");
  if (!gl) throw new Error("WebGL 2 not supported");

  gl.enable(gl.DEPTH_TEST);
  gl.depthFunc(gl.LEQUAL);

  const program = createProgram(gl, vertSrc, fragSrc);
  gl.useProgram(program);

  const u = {
    model: gl.getUniformLocation(program, "uModel")!,
    view: gl.getUniformLocation(program, "uView")!,
    projection: gl.getUniformLocation(program, "uProjection")!,
    color: gl.getUniformLocation(program, "uColor")!,
    lightDir: gl.getUniformLocation(program, "uLightDir")!,
    ambient: gl.getUniformLocation(program, "uAmbient")!,
    roughness: gl.getUniformLocation(program, "uRoughness")!,
    metallic: gl.getUniformLocation(program, "uMetallic")!,
    cameraPos: gl.getUniformLocation(program, "uCameraPos")!,
    shadowMatrix: gl.getUniformLocation(program, "uShadowMatrix")!,
    shadowMap: gl.getUniformLocation(program, "uShadowMap")!,
  };

  // Shadow map setup
  const SHADOW_SIZE = 2048;
  const shadowProgram = createProgram(gl, shadowVertSrc, shadowFragSrc);
  const shadowFbo = gl.createFramebuffer()!;
  const shadowDepthTex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, shadowDepthTex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.DEPTH_COMPONENT32F, SHADOW_SIZE, SHADOW_SIZE, 0, gl.DEPTH_COMPONENT, gl.FLOAT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_MODE, gl.COMPARE_REF_TO_TEXTURE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_COMPARE_FUNC, gl.LEQUAL);
  gl.bindFramebuffer(gl.FRAMEBUFFER, shadowFbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, shadowDepthTex, 0);
  gl.drawBuffers([gl.NONE]);
  gl.readBuffer(gl.NONE);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.bindTexture(gl.TEXTURE_2D, null);

  const shadow = {
    program: shadowProgram,
    uModel: gl.getUniformLocation(shadowProgram, "uModel")!,
    uLightVP: gl.getUniformLocation(shadowProgram, "uLightVP")!,
    fbo: shadowFbo,
    depthTex: shadowDepthTex,
    size: SHADOW_SIZE,
  };

  // Directional light from above-right
  const lx = 0.3,
    ly = 0.9,
    lz = 0.2;
  const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
  gl.uniform3f(u.lightDir, lx / lLen, ly / lLen, lz / lLen);
  gl.uniform3f(u.ambient, 0.3, 0.3, 0.35);

  // Build track geometry
  const cl = track.centerline;
  const hw = track.halfWidth;
  const heights = track.heights;
  const laterals = computeLateralDirs(cl);
  const terrainNormals = computeTerrainNormals(cl, laterals, heights);

  const ground = createMesh(gl, program, buildGround(), gl.TRIANGLES);
  const gravel = createMesh(
    gl,
    program,
    buildStrip3D(cl, laterals, hw + 4, heights, terrainNormals, -0.01),
    gl.TRIANGLE_STRIP
  );
  const surface = createMesh(
    gl,
    program,
    buildStrip3D(cl, laterals, hw, heights, terrainNormals, 0.0),
    gl.TRIANGLE_STRIP
  );

  const curbOuter = hw + 1.2;
  const curbL = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw, curbOuter, 1, heights, terrainNormals, 0.005),
    gl.TRIANGLE_STRIP
  );
  const curbR = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw, curbOuter, -1, heights, terrainNormals, 0.005),
    gl.TRIANGLE_STRIP
  );

  const lineHW = 0.12;
  const edgeL = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw - lineHW, hw + lineHW, 1, heights, terrainNormals, 0.01),
    gl.TRIANGLE_STRIP
  );
  const edgeR = createMesh(
    gl,
    program,
    buildSideStrip3D(cl, laterals, hw - lineHW, hw + lineHW, -1, heights, terrainNormals, 0.01),
    gl.TRIANGLE_STRIP
  );

  // Checkered start/finish line
  const checker = buildCheckerboard(cl, laterals, hw, track.startFinishIndex, heights);
  const startFinishWhite = createMesh(gl, program, checker.white, gl.TRIANGLES);
  const startFinishBlack = createMesh(gl, program, checker.black, gl.TRIANGLES);

  // Center line dashes
  const centerDashes = createMesh(
    gl,
    program,
    buildCenterDashes(cl, laterals, track.startFinishIndex, heights),
    gl.TRIANGLES
  );

  const box = createMesh(gl, program, buildBox(), gl.TRIANGLES);
  const cylinder = createMesh(gl, program, buildCylinder(16), gl.TRIANGLES);

  // Sky program + fullscreen quad
  const skyProgram = createProgram(gl, skyVertSrc, skyFragSrc);
  const skyVao = gl.createVertexArray()!;
  gl.bindVertexArray(skyVao);
  const skyVbo = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, skyVbo);
  // prettier-ignore
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  1,  1,
    -1, -1,  1,  1, -1,  1,
  ]), gl.STATIC_DRAW);
  const skyAPos = gl.getAttribLocation(skyProgram, "aPos");
  gl.enableVertexAttribArray(skyAPos);
  gl.vertexAttribPointer(skyAPos, 2, gl.FLOAT, false, 8, 0);
  gl.bindVertexArray(null);
  const skyQuad: StaticMesh = { vao: skyVao, count: 6, mode: gl.TRIANGLES };

  // Initialize camera at driver's eye position
  const startX = track.startX;
  const startZ = -track.startY;
  const cam: CameraState = {
    x: startX + 0.15,
    y: 1.1,
    z: startZ,
    tx: startX + 50,
    ty: 0.94,
    tz: startZ,
    mode: "cockpit",
  };

  // Post-processing is built lazily after first paint — see ensurePostProcess.
  const pp = null;
  // Software renderers (the web-health SwiftShader probe, GPU-less machines)
  // can't afford post-processing — gate it off up front so it's never built.
  const allowPostProcess = !isSoftwareRenderer(gl);

  return {
    gl,
    canvas,
    program,
    u,
    ground,
    gravel,
    surface,
    curbL,
    curbR,
    edgeL,
    edgeR,
    startFinishWhite,
    startFinishBlack,
    centerDashes,
    box,
    cylinder,
    shadow,
    skyProgram,
    skyQuad,
    cam,
    pp,
    allowPostProcess,
    postProcessEnabled: allowPostProcess,
  };
}

// --- Shadow pass ---

function renderShadowPass(r: Renderer, car: Car): Mat4 {
  const { gl } = r;

  // Light direction (same as scene light)
  const lx = 0.3, ly = 0.9, lz = 0.2;
  const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
  const ldx = lx / lLen, ldy = ly / lLen, ldz = lz / lLen;

  // Center shadow frustum on car position
  const carX = car.x;
  const carZ = -car.y;
  const shadowDist = 150;

  // Light view matrix: look from far away along light direction toward car
  const lightEye: [number, number, number] = [
    carX + ldx * shadowDist,
    car.z + ldy * shadowDist,
    carZ + ldz * shadowDist,
  ];
  const lightTarget: [number, number, number] = [carX, car.z, carZ];
  const lightView = mat4LookAt(lightEye, lightTarget, [0, 1, 0]);

  // Orthographic projection covering the area around the car
  const extent = 80;
  const lightProj = mat4Ortho(-extent, extent, -extent, extent, 1, shadowDist * 2);
  const lightVP = mat4Multiply(lightProj, lightView);

  // Bias matrix: transform from [-1,1] clip space to [0,1] texture coords
  // (applied in the vertex shader via uShadowMatrix = biasMatrix * lightVP)

  // Render shadow pass
  gl.bindFramebuffer(gl.FRAMEBUFFER, r.shadow.fbo);
  gl.viewport(0, 0, r.shadow.size, r.shadow.size);
  gl.clear(gl.DEPTH_BUFFER_BIT);

  gl.useProgram(r.shadow.program);
  gl.uniformMatrix4fv(r.shadow.uLightVP, false, lightVP);

  const identity = mat4Create();

  // Draw scene geometry into shadow map (track + car)
  drawShadowMesh(r, r.ground, identity);
  drawShadowMesh(r, r.surface, identity);
  drawShadowMesh(r, r.gravel, identity);

  // Draw car parts
  const carModel = mat4Multiply(
    mat4FromTranslation(car.x, car.z, -car.y),
    mat4FromRotationY(car.angle)
  );
  for (let i = 0; i < CAR_PARTS.length; i++) {
    const part = CAR_PARTS[i];
    const partModel = mat4Multiply(
      mat4Multiply(carModel, mat4FromTranslation(part.cx, part.cy, part.cz)),
      mat4FromScaling(part.sx, part.sy, part.sz)
    );
    const mesh = part.mesh === "cylinder" ? r.cylinder : r.box;
    drawShadowMesh(r, mesh, partModel);
  }

  // Restore default framebuffer
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  return lightVP;
}

function drawShadowMesh(r: Renderer, mesh: StaticMesh, model: Mat4): void {
  const { gl } = r;
  gl.uniformMatrix4fv(r.shadow.uModel, false, model);
  gl.bindVertexArray(mesh.vao);
  gl.drawArrays(mesh.mode, 0, mesh.count);
}

// --- Render ---

export function renderScene(
  r: Renderer,
  car: Car,
  _track: Track,
  _alpha: number,
  gyroRoll: number = 0,
  steerInput: number = 0
): { view: Mat4; projection: Mat4 } {
  const { gl, canvas } = r;

  // Shadow pass: render depth from sun's perspective
  const lightVP = renderShadowPass(r, car);

  // Main pass — render into the post-process scene FBO when PP is built and on
  // (particles follow before composite); otherwise straight to the canvas.
  gl.bindFramebuffer(
    gl.FRAMEBUFFER,
    r.pp && r.postProcessEnabled ? r.pp.sceneFbo : null
  );
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.clear(gl.DEPTH_BUFFER_BIT | gl.COLOR_BUFFER_BIT);

  // Draw sky gradient (fullscreen quad, no depth test)
  gl.disable(gl.DEPTH_TEST);
  gl.useProgram(r.skyProgram);
  gl.bindVertexArray(r.skyQuad.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
  gl.enable(gl.DEPTH_TEST);

  gl.useProgram(r.program);

  // Bind shadow map to texture unit 0
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, r.shadow.depthTex);
  gl.uniform1i(r.u.shadowMap, 0);
  gl.uniformMatrix4fv(r.u.shadowMatrix, false, lightVP);

  // Speed-dependent FOV: 60° at rest → 75° at 300 km/h
  const speedMs = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
  const speedKmh = speedMs * 3.6;
  const speedFrac = Math.min(speedKmh / 300, 1);
  const baseFov = Math.PI / 3; // 60°
  const maxFov = (75 * Math.PI) / 180; // 75°
  const fov = baseFov + (maxFov - baseFov) * speedFrac;

  const aspect = canvas.width / canvas.height;
  const proj = mat4Perspective(fov, aspect, 0.1, 1000);
  gl.uniformMatrix4fv(r.u.projection, false, proj);

  // Camera
  updateCamera(r.cam, car, speedFrac);
  let view = mat4LookAt(
    [r.cam.x, r.cam.y, r.cam.z],
    [r.cam.tx, r.cam.ty, r.cam.tz],
    [0, 1, 0]
  );
  // Apply camera roll from device tilt (pre-multiply rolls in view space)
  if (gyroRoll !== 0) {
    view = mat4Multiply(mat4FromRotationZ(-gyroRoll), view);
  }
  gl.uniformMatrix4fv(r.u.view, false, view);

  // Set camera position for PBR specular calculations
  gl.uniform3f(r.u.cameraPos, r.cam.x, r.cam.y, r.cam.z);

  const identity = mat4Create();

  // Ground (grass — very rough, non-metallic)
  drawMesh(r, r.ground, identity, [0.18, 0.42, 0.14, 1.0], 0.95, 0.0);

  // Gravel (rough aggregate)
  drawMesh(r, r.gravel, identity, [0.65, 0.55, 0.35, 1.0], 0.90, 0.0);

  // Track surface (asphalt — rough but slightly reflective when wet)
  drawMesh(r, r.surface, identity, [0.2, 0.2, 0.22, 1.0], 0.82, 0.0);

  // Curbs (painted concrete)
  drawMesh(r, r.curbL, identity, [0.85, 0.15, 0.1, 1.0], 0.70, 0.0);
  drawMesh(r, r.curbR, identity, [0.85, 0.15, 0.1, 1.0], 0.70, 0.0);

  // Edge lines (road paint — semi-glossy)
  drawMesh(r, r.edgeL, identity, [1.0, 1.0, 1.0, 1.0], 0.55, 0.0);
  drawMesh(r, r.edgeR, identity, [1.0, 1.0, 1.0, 1.0], 0.55, 0.0);

  // Checkered start/finish line
  drawMesh(r, r.startFinishWhite, identity, [1.0, 1.0, 1.0, 1.0], 0.55, 0.0);
  drawMesh(r, r.startFinishBlack, identity, [0.05, 0.05, 0.05, 1.0], 0.55, 0.0);

  // Center line dashes
  drawMesh(r, r.centerDashes, identity, [1.0, 1.0, 1.0, 1.0], 0.55, 0.0);

  // Car
  drawCar(r, car, steerInput);

  return { view, projection: proj };
}

function drawMesh(
  r: Renderer,
  mesh: StaticMesh,
  model: Mat4,
  color: readonly number[],
  roughness: number = 0.5,
  metallic: number = 0.0
): void {
  const { gl } = r;
  gl.uniformMatrix4fv(r.u.model, false, model);
  gl.uniform4fv(r.u.color, color);
  gl.uniform1f(r.u.roughness, roughness);
  gl.uniform1f(r.u.metallic, metallic);
  gl.bindVertexArray(mesh.vao);
  gl.drawArrays(mesh.mode, 0, mesh.count);
}

// --- Camera ---

function updateCamera(cam: CameraState, car: Car, speedFrac: number): void {
  const carX = car.x;
  const carZ = -car.y;
  const cosA = Math.cos(car.angle);
  const sinA = Math.sin(car.angle);

  let desiredX: number, desiredY: number, desiredZ: number;
  let targetX: number, targetY: number, targetZ: number;

  if (cam.mode === "cockpit") {
    // First-person: driver's eye position
    const eyeForward = 0.15;
    const eyeHeight = 1.1;
    desiredX = carX + cosA * eyeForward;
    desiredY = car.z + eyeHeight;
    desiredZ = carZ - sinA * eyeForward;

    // Speed-dependent look-ahead: 50m at rest → 80m at top speed
    const lookAhead = 50 + 30 * speedFrac;
    targetX = carX + cosA * lookAhead;
    targetY = car.z + eyeHeight * 0.85;
    targetZ = carZ - sinA * lookAhead;
  } else if (cam.mode === "chase") {
    // Chase camera: behind and above the car, follows heading
    const chaseDist = 10 + 4 * speedFrac; // 10-14m behind
    const chaseHeight = 4 + 2 * speedFrac; // 4-6m up
    desiredX = carX - cosA * chaseDist;
    desiredY = car.z + chaseHeight;
    desiredZ = carZ + sinA * chaseDist;

    // Look slightly ahead of the car
    const lookAhead = 8 + 12 * speedFrac;
    targetX = carX + cosA * lookAhead;
    targetY = car.z + 0.5;
    targetZ = carZ - sinA * lookAhead;
  } else {
    // Chase-north: fixed direction camera (always from south, looking north)
    const chaseDist = 10 + 4 * speedFrac;
    const chaseHeight = 4 + 2 * speedFrac;
    desiredX = carX;
    desiredY = car.z + chaseHeight;
    desiredZ = carZ + chaseDist; // always south of car (+Z direction)

    targetX = carX;
    targetY = car.z + 0.5;
    targetZ = carZ - 5; // look slightly ahead (north)
  }

  const lerp = 0.12;
  cam.x += (desiredX - cam.x) * lerp;
  cam.y += (desiredY - cam.y) * lerp;
  cam.z += (desiredZ - cam.z) * lerp;
  cam.tx += (targetX - cam.tx) * lerp;
  cam.ty += (targetY - cam.ty) * lerp;
  cam.tz += (targetZ - cam.tz) * lerp;
}

export function setCameraMode(r: Renderer, mode: CameraMode): void {
  r.cam.mode = mode;
}

export function getCameraMode(r: Renderer): CameraMode {
  return r.cam.mode;
}

// --- Car ---

interface CarPart {
  cx: number;
  cy: number;
  cz: number;
  sx: number;
  sy: number;
  sz: number;
  color: readonly number[];
  mesh?: "box" | "cylinder";
  roughness?: number;
  metallic?: number;
}

// Material definitions: [r, g, b, a, roughness, metallic]
const CAR_RED: readonly number[] = [0.80, 0.08, 0.08, 1.0];
const CAR_DARK_RED: readonly number[] = [0.60, 0.06, 0.06, 1.0];
const CAR_WING: readonly number[] = [0.15, 0.15, 0.16, 1.0];
const CAR_DARK: readonly number[] = [0.02, 0.02, 0.02, 1.0];
const CAR_YELLOW: readonly number[] = [0.95, 0.80, 0.05, 1.0];
const CAR_SILVER: readonly number[] = [0.7, 0.7, 0.72, 1.0];
const CAR_TIRE: readonly number[] = [0.10, 0.10, 0.10, 1.0];
const CAR_RIM: readonly number[] = [0.45, 0.45, 0.48, 1.0];

// Default PBR material properties per color palette
const CAR_MATERIALS = new Map<readonly number[], { roughness: number; metallic: number }>([
  [CAR_RED, { roughness: 0.28, metallic: 0.85 }],       // metallic paint
  [CAR_DARK_RED, { roughness: 0.32, metallic: 0.80 }],  // metallic paint (sidepods)
  [CAR_WING, { roughness: 0.40, metallic: 0.25 }],      // carbon fiber composite
  [CAR_DARK, { roughness: 0.50, metallic: 0.10 }],      // matte carbon / cockpit
  [CAR_YELLOW, { roughness: 0.30, metallic: 0.75 }],    // painted accent
  [CAR_SILVER, { roughness: 0.25, metallic: 0.90 }],    // polished metal suspension
  [CAR_TIRE, { roughness: 0.65, metallic: 0.0 }],       // rubber
  [CAR_RIM, { roughness: 0.20, metallic: 0.92 }],       // polished alloy
]);

const CAR_PARTS: CarPart[] = [
  // --- Monocoque / survival cell ---
  // Main body tub (wide rear, tapers to front)
  { cx: -0.4, cy: 0.28, cz: 0, sx: 3.0, sy: 0.34, sz: 1.05, color: CAR_RED },
  // Upper body fairing
  { cx: -0.5, cy: 0.48, cz: 0, sx: 2.2, sy: 0.12, sz: 0.85, color: CAR_RED },

  // --- Nose section ---
  // Nose main
  { cx: 1.7, cy: 0.22, cz: 0, sx: 1.6, sy: 0.18, sz: 0.55, color: CAR_RED },
  // Nose tip (tapered, yellow accent)
  { cx: 2.65, cy: 0.16, cz: 0, sx: 0.5, sy: 0.10, sz: 0.28, color: CAR_YELLOW },

  // --- Cockpit ---
  // Opening (dark recess)
  { cx: 0.4, cy: 0.55, cz: 0, sx: 0.85, sy: 0.05, sz: 0.48, color: CAR_DARK },
  // Headrest
  { cx: -0.1, cy: 0.58, cz: 0, sx: 0.28, sy: 0.10, sz: 0.30, color: CAR_RED },

  // --- Halo device ---
  // Center column (front pillar)
  { cx: 0.7, cy: 0.58, cz: 0, sx: 0.06, sy: 0.18, sz: 0.06, color: CAR_DARK },
  // Top hoop (runs above driver's head)
  { cx: 0.35, cy: 0.68, cz: 0, sx: 0.8, sy: 0.04, sz: 0.06, color: CAR_DARK },
  // Left pillar (rear)
  { cx: -0.05, cy: 0.58, cz: -0.22, sx: 0.06, sy: 0.20, sz: 0.04, color: CAR_DARK },
  // Right pillar (rear)
  { cx: -0.05, cy: 0.58, cz: 0.22, sx: 0.06, sy: 0.20, sz: 0.04, color: CAR_DARK },

  // --- Airbox (engine air intake above driver) ---
  { cx: -0.25, cy: 0.68, cz: 0, sx: 0.40, sy: 0.22, sz: 0.30, color: CAR_RED },
  // T-cam (yellow camera pod on top)
  { cx: -0.25, cy: 0.82, cz: 0, sx: 0.10, sy: 0.06, sz: 0.08, color: CAR_YELLOW },

  // --- Engine cover (sloping from airbox to rear) ---
  { cx: -1.2, cy: 0.42, cz: 0, sx: 1.5, sy: 0.26, sz: 0.65, color: CAR_RED },
  // Shark fin (vertical stabilizer on engine cover)
  { cx: -1.2, cy: 0.60, cz: 0, sx: 1.0, sy: 0.12, sz: 0.02, color: CAR_RED },

  // --- Side pods ---
  // Left sidepod
  { cx: -0.3, cy: 0.22, cz: -0.68, sx: 1.8, sy: 0.20, sz: 0.35, color: CAR_DARK_RED },
  // Right sidepod
  { cx: -0.3, cy: 0.22, cz: 0.68, sx: 1.8, sy: 0.20, sz: 0.35, color: CAR_DARK_RED },
  // Sidepod inlets (darker mouth)
  { cx: 0.6, cy: 0.25, cz: -0.70, sx: 0.15, sy: 0.16, sz: 0.30, color: CAR_DARK },
  { cx: 0.6, cy: 0.25, cz: 0.70, sx: 0.15, sy: 0.16, sz: 0.30, color: CAR_DARK },

  // --- Floor ---
  { cx: -0.3, cy: 0.03, cz: 0, sx: 4.0, sy: 0.03, sz: 1.7, color: CAR_DARK },
  // Floor edges (slightly raised, visible from side)
  { cx: -0.3, cy: 0.06, cz: -0.88, sx: 3.2, sy: 0.04, sz: 0.06, color: CAR_DARK },
  { cx: -0.3, cy: 0.06, cz: 0.88, sx: 3.2, sy: 0.04, sz: 0.06, color: CAR_DARK },

  // --- Front wing ---
  // Main plane
  { cx: 2.75, cy: 0.06, cz: 0, sx: 0.40, sy: 0.03, sz: 2.0, color: CAR_WING },
  // Second element (above main)
  { cx: 2.70, cy: 0.10, cz: 0, sx: 0.30, sy: 0.02, sz: 1.85, color: CAR_WING },
  // Endplates
  { cx: 2.72, cy: 0.08, cz: -1.0, sx: 0.40, sy: 0.12, sz: 0.03, color: CAR_WING },
  { cx: 2.72, cy: 0.08, cz: 1.0, sx: 0.40, sy: 0.12, sz: 0.03, color: CAR_WING },

  // --- Rear wing ---
  // Main plane
  { cx: -2.65, cy: 0.78, cz: 0, sx: 0.30, sy: 0.05, sz: 1.5, color: CAR_WING },
  // DRS flap
  { cx: -2.60, cy: 0.86, cz: 0, sx: 0.22, sy: 0.025, sz: 1.4, color: CAR_WING },
  // Endplates
  { cx: -2.65, cy: 0.70, cz: -0.76, sx: 0.35, sy: 0.32, sz: 0.03, color: CAR_WING },
  { cx: -2.65, cy: 0.70, cz: 0.76, sx: 0.35, sy: 0.32, sz: 0.03, color: CAR_WING },
  // Support pillars (swan-neck)
  { cx: -2.35, cy: 0.58, cz: -0.20, sx: 0.04, sy: 0.26, sz: 0.03, color: CAR_WING },
  { cx: -2.35, cy: 0.58, cz: 0.20, sx: 0.04, sy: 0.26, sz: 0.03, color: CAR_WING },

  // --- Rear diffuser ---
  { cx: -2.2, cy: 0.10, cz: 0, sx: 0.55, sy: 0.14, sz: 1.5, color: CAR_DARK },

  // --- Rear wheels (cylinders) ---
  { cx: -1.65, cy: 0.36, cz: -1.00, sx: 0.72, sy: 0.72, sz: 0.40, color: CAR_TIRE, mesh: "cylinder" },
  { cx: -1.65, cy: 0.36, cz: 1.00, sx: 0.72, sy: 0.72, sz: 0.40, color: CAR_TIRE, mesh: "cylinder" },
  // Rear wheel covers (slightly smaller diameter discs)
  { cx: -1.65, cy: 0.36, cz: -1.22, sx: 0.58, sy: 0.58, sz: 0.02, color: CAR_RIM, mesh: "cylinder" },
  { cx: -1.65, cy: 0.36, cz: 1.22, sx: 0.58, sy: 0.58, sz: 0.02, color: CAR_RIM, mesh: "cylinder" },
  // Rear rims (inner visible face)
  { cx: -1.65, cy: 0.36, cz: -0.78, sx: 0.58, sy: 0.58, sz: 0.02, color: CAR_RIM, mesh: "cylinder" },
  { cx: -1.65, cy: 0.36, cz: 0.78, sx: 0.58, sy: 0.58, sz: 0.02, color: CAR_RIM, mesh: "cylinder" },

  // --- Front wheels (cylinders, steered) ---
  { cx: 1.55, cy: 0.33, cz: -0.82, sx: 0.66, sy: 0.66, sz: 0.305, color: CAR_TIRE, mesh: "cylinder" },
  { cx: 1.55, cy: 0.33, cz: 0.82, sx: 0.66, sy: 0.66, sz: 0.305, color: CAR_TIRE, mesh: "cylinder" },
  // Front wheel covers
  { cx: 1.55, cy: 0.33, cz: -0.98, sx: 0.52, sy: 0.52, sz: 0.02, color: CAR_RIM, mesh: "cylinder" },
  { cx: 1.55, cy: 0.33, cz: 0.98, sx: 0.52, sy: 0.52, sz: 0.02, color: CAR_RIM, mesh: "cylinder" },

  // --- Front suspension arms (thin rods to front wheels) ---
  // Upper wishbone
  { cx: 1.2, cy: 0.32, cz: -0.52, sx: 0.9, sy: 0.02, sz: 0.02, color: CAR_SILVER },
  { cx: 1.2, cy: 0.32, cz: 0.52, sx: 0.9, sy: 0.02, sz: 0.02, color: CAR_SILVER },
  // Lower wishbone
  { cx: 1.1, cy: 0.18, cz: -0.52, sx: 0.8, sy: 0.02, sz: 0.02, color: CAR_SILVER },
  { cx: 1.1, cy: 0.18, cz: 0.52, sx: 0.8, sy: 0.02, sz: 0.02, color: CAR_SILVER },

  // --- Rear suspension arms ---
  { cx: -1.3, cy: 0.32, cz: -0.65, sx: 0.7, sy: 0.02, sz: 0.02, color: CAR_SILVER },
  { cx: -1.3, cy: 0.32, cz: 0.65, sx: 0.7, sy: 0.02, sz: 0.02, color: CAR_SILVER },
  { cx: -1.3, cy: 0.18, cz: -0.65, sx: 0.6, sy: 0.02, sz: 0.02, color: CAR_SILVER },
  { cx: -1.3, cy: 0.18, cz: 0.65, sx: 0.6, sy: 0.02, sz: 0.02, color: CAR_SILVER },

  // --- Bargeboard / side deflectors ---
  { cx: 0.8, cy: 0.18, cz: -0.52, sx: 0.5, sy: 0.12, sz: 0.02, color: CAR_WING },
  { cx: 0.8, cy: 0.18, cz: 0.52, sx: 0.5, sy: 0.12, sz: 0.02, color: CAR_WING },

  // --- Exhaust pipe (small dark cylinder at rear) ---
  { cx: -2.0, cy: 0.38, cz: 0, sx: 0.15, sy: 0.08, sz: 0.08, color: CAR_DARK, mesh: "cylinder" },

  // --- Rain light (red, at rear center) ---
  { cx: -2.45, cy: 0.25, cz: 0, sx: 0.06, sy: 0.06, sz: 0.20, color: [1.0, 0.0, 0.0, 1.0] },
];

// Front wheel parts: tires (38,39) + covers (40,41)
const FRONT_WHEEL_INDICES = [38, 39, 40, 41];

function drawCar(r: Renderer, car: Car, steerInput: number = 0): void {
  // Car model matrix: translate to 3D position, rotate to heading
  // Physics (x, y) → 3D (x, car.z, -y), rotateY(angle)
  const carModel = mat4Multiply(
    mat4FromTranslation(car.x, car.z, -car.y),
    mat4FromRotationY(car.angle)
  );

  // Visual steering angle for front wheels (negate to match physics sign convention)
  const wheelSteerAngle = -steerInput * car.maxSteerAngle;

  for (let i = 0; i < CAR_PARTS.length; i++) {
    const part = CAR_PARTS[i];
    let partModel: Mat4;

    if (FRONT_WHEEL_INDICES.includes(i)) {
      // Front wheels: translate to position, rotate by steering, then scale
      partModel = mat4Multiply(
        mat4Multiply(
          mat4Multiply(carModel, mat4FromTranslation(part.cx, part.cy, part.cz)),
          mat4FromRotationY(wheelSteerAngle)
        ),
        mat4FromScaling(part.sx, part.sy, part.sz)
      );
    } else {
      partModel = mat4Multiply(
        mat4Multiply(carModel, mat4FromTranslation(part.cx, part.cy, part.cz)),
        mat4FromScaling(part.sx, part.sy, part.sz)
      );
    }
    const mesh = part.mesh === "cylinder" ? r.cylinder : r.box;
    // Look up PBR material from palette, fall back to part overrides or defaults
    const mat = CAR_MATERIALS.get(part.color);
    const roughness = part.roughness ?? mat?.roughness ?? 0.5;
    const metallic = part.metallic ?? mat?.metallic ?? 0.0;
    drawMesh(r, mesh, partModel, part.color, roughness, metallic);
  }
}

// --- Geometry builders ---

// Compute lateral directions (perpendicular to centerline) in 2D
function computeLateralDirs(
  cl: { x: number; y: number }[]
): { nx: number; ny: number }[] {
  const normals: { nx: number; ny: number }[] = [];
  for (let i = 0; i < cl.length; i++) {
    const prev = cl[(i - 1 + cl.length) % cl.length];
    const next = cl[(i + 1) % cl.length];
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const len = Math.sqrt(tx * tx + ty * ty) || 1;
    normals.push({ nx: -ty / len, ny: tx / len });
  }
  return normals;
}

// Compute per-point surface normals using terrain slope (cross product of tangent × lateral).
function computeTerrainNormals(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  heights: number[]
): { nx: number; ny: number; nz: number }[] {
  const n = cl.length;
  const normals: { nx: number; ny: number; nz: number }[] = [];
  for (let i = 0; i < n; i++) {
    const prev = (i - 1 + n) % n;
    const next = (i + 1) % n;
    // Forward tangent in 3D (central difference, y→-z mapping)
    const tx = cl[next].x - cl[prev].x;
    const ty = heights[next] - heights[prev];
    const tz = -(cl[next].y - cl[prev].y);
    const tLen = Math.sqrt(tx * tx + ty * ty + tz * tz) || 1;
    const tnx = tx / tLen, tny = ty / tLen, tnz = tz / tLen;
    // Lateral in 3D
    const lx = laterals[i].nx;
    const lz = -laterals[i].ny;
    // Normal = cross(tangent, lateral)
    const nx = tny * lz;
    const ny = tnz * lx - tnx * lz;
    const nz = -tny * lx;
    const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    normals.push({ nx: nx / nLen, ny: ny / nLen, nz: nz / nLen });
  }
  return normals;
}

// Build a symmetric track strip (centered on centerline)
function buildStrip3D(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  halfWidth: number,
  heights: number[],
  terrainNormals: { nx: number; ny: number; nz: number }[],
  heightOffset: number
): Float32Array {
  const data: number[] = [];
  for (let i = 0; i < cl.length; i++) {
    const cx = cl[i].x;
    const cz = -cl[i].y;
    const nx = laterals[i].nx;
    const nz = -laterals[i].ny;
    const cy = heights[i] + heightOffset;
    const nnx = terrainNormals[i].nx;
    const nny = terrainNormals[i].ny;
    const nnz = terrainNormals[i].nz;
    data.push(cx + nx * halfWidth, cy, cz + nz * halfWidth, nnx, nny, nnz);
    data.push(cx - nx * halfWidth, cy, cz - nz * halfWidth, nnx, nny, nnz);
  }
  // Close the loop
  data.push(data[0], data[1], data[2], data[3], data[4], data[5]);
  data.push(data[6], data[7], data[8], data[9], data[10], data[11]);
  return new Float32Array(data);
}

// Build a one-sided strip (e.g. curbs, edge lines)
function buildSideStrip3D(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  innerHW: number,
  outerHW: number,
  side: 1 | -1,
  heights: number[],
  terrainNormals: { nx: number; ny: number; nz: number }[],
  heightOffset: number
): Float32Array {
  const data: number[] = [];
  for (let i = 0; i < cl.length; i++) {
    const cx = cl[i].x;
    const cz = -cl[i].y;
    const nx = laterals[i].nx;
    const nz = -laterals[i].ny;
    const cy = heights[i] + heightOffset;
    const nnx = terrainNormals[i].nx;
    const nny = terrainNormals[i].ny;
    const nnz = terrainNormals[i].nz;
    data.push(cx + nx * innerHW * side, cy, cz + nz * innerHW * side, nnx, nny, nnz);
    data.push(cx + nx * outerHW * side, cy, cz + nz * outerHW * side, nnx, nny, nnz);
  }
  // Close
  data.push(data[0], data[1], data[2], data[3], data[4], data[5]);
  data.push(data[6], data[7], data[8], data[9], data[10], data[11]);
  return new Float32Array(data);
}

// Build a checkered start/finish line: returns separate white and black square meshes
function buildCheckerboard(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  halfWidth: number,
  index: number,
  heights: number[]
): { white: Float32Array; black: Float32Array } {
  const cx = cl[index].x;
  const cz = -cl[index].y;
  const lx = laterals[index].nx;
  const lz = -laterals[index].ny;

  // Tangent direction along the track
  const prev = cl[(index - 1 + cl.length) % cl.length];
  const next = cl[(index + 1) % cl.length];
  const tx = next.x - prev.x;
  const ty = next.y - prev.y;
  const tLen = Math.sqrt(tx * tx + ty * ty) || 1;
  const tdx = tx / tLen;
  const tdz = -ty / tLen;

  const lineHalfWidth = 1.5; // 3m wide checkerboard
  const h = heights[index] + 0.02;
  const squareSize = 1.0; // 1m squares
  const cols = Math.round((2 * halfWidth) / squareSize);
  const rows = Math.round((2 * lineHalfWidth) / squareSize);

  const whiteVerts: number[] = [];
  const blackVerts: number[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const verts = (r + c) % 2 === 0 ? whiteVerts : blackVerts;

      // Lateral: from +halfWidth to -halfWidth
      const lat0 = halfWidth - c * squareSize;
      const lat1 = halfWidth - (c + 1) * squareSize;
      // Tangent: from -lineHalfWidth to +lineHalfWidth
      const tan0 = -lineHalfWidth + r * squareSize;
      const tan1 = -lineHalfWidth + (r + 1) * squareSize;

      // Four corners
      const ax = cx + lx * lat0 + tdx * tan0;
      const az = cz + lz * lat0 + tdz * tan0;
      const bx = cx + lx * lat0 + tdx * tan1;
      const bz = cz + lz * lat0 + tdz * tan1;
      const dx = cx + lx * lat1 + tdx * tan1;
      const dz = cz + lz * lat1 + tdz * tan1;
      const ex = cx + lx * lat1 + tdx * tan0;
      const ez = cz + lz * lat1 + tdz * tan0;

      // Two triangles (6 vertices, each with pos + normal)
      verts.push(
        ax, h, az, 0, 1, 0, bx, h, bz, 0, 1, 0, dx, h, dz, 0, 1, 0,
        ax, h, az, 0, 1, 0, dx, h, dz, 0, 1, 0, ex, h, ez, 0, 1, 0
      );
    }
  }

  return {
    white: new Float32Array(whiteVerts),
    black: new Float32Array(blackVerts),
  };
}

// Build dashed center line along the track
function buildCenterDashes(
  cl: { x: number; y: number }[],
  laterals: { nx: number; ny: number }[],
  startFinishIndex: number,
  heights: number[]
): Float32Array {
  const data: number[] = [];
  const dashHW = 0.1; // 0.2m wide

  for (let i = 0; i < cl.length; i++) {
    // Dash every other segment
    if (i % 2 !== 0) continue;

    // Skip near start/finish line
    const dist = Math.min(
      Math.abs(i - startFinishIndex),
      cl.length - Math.abs(i - startFinishIndex)
    );
    if (dist < 3) continue;

    const j = (i + 1) % cl.length;
    const cx1 = cl[i].x, cz1 = -cl[i].y;
    const nx1 = laterals[i].nx, nz1 = -laterals[i].ny;
    const cx2 = cl[j].x, cz2 = -cl[j].y;
    const nx2 = laterals[j].nx, nz2 = -laterals[j].ny;
    const h1 = heights[i] + 0.01;
    const h2 = heights[j] + 0.01;

    // Quad from point i to point i+1, offset ±dashHW from center
    const x1 = cx1 + nx1 * dashHW, z1 = cz1 + nz1 * dashHW;
    const x2 = cx1 - nx1 * dashHW, z2 = cz1 - nz1 * dashHW;
    const x3 = cx2 - nx2 * dashHW, z3 = cz2 - nz2 * dashHW;
    const x4 = cx2 + nx2 * dashHW, z4 = cz2 + nz2 * dashHW;

    data.push(
      x1, h1, z1, 0, 1, 0, x4, h2, z4, 0, 1, 0, x3, h2, z3, 0, 1, 0,
      x1, h1, z1, 0, 1, 0, x3, h2, z3, 0, 1, 0, x2, h1, z2, 0, 1, 0
    );
  }

  return new Float32Array(data);
}

function buildGround(): Float32Array {
  const s = 500;
  const y = -0.05;
  // Two triangles forming a large quad
  // prettier-ignore
  return new Float32Array([
    -s, y, -s, 0, 1, 0,   s, y, -s, 0, 1, 0,   s, y,  s, 0, 1, 0,
    -s, y, -s, 0, 1, 0,   s, y,  s, 0, 1, 0,  -s, y,  s, 0, 1, 0,
  ]);
}

function buildBox(): Float32Array {
  // Unit box centered at origin, each vertex: pos(3) + normal(3)
  // prettier-ignore
  return new Float32Array([
    // +Y face (top)
    -0.5,  0.5, -0.5,  0,  1,  0,   0.5,  0.5, -0.5,  0,  1,  0,   0.5,  0.5,  0.5,  0,  1,  0,
    -0.5,  0.5, -0.5,  0,  1,  0,   0.5,  0.5,  0.5,  0,  1,  0,  -0.5,  0.5,  0.5,  0,  1,  0,
    // -Y face (bottom)
    -0.5, -0.5,  0.5,  0, -1,  0,   0.5, -0.5,  0.5,  0, -1,  0,   0.5, -0.5, -0.5,  0, -1,  0,
    -0.5, -0.5,  0.5,  0, -1,  0,   0.5, -0.5, -0.5,  0, -1,  0,  -0.5, -0.5, -0.5,  0, -1,  0,
    // +X face (front)
     0.5, -0.5, -0.5,  1,  0,  0,   0.5,  0.5, -0.5,  1,  0,  0,   0.5,  0.5,  0.5,  1,  0,  0,
     0.5, -0.5, -0.5,  1,  0,  0,   0.5,  0.5,  0.5,  1,  0,  0,   0.5, -0.5,  0.5,  1,  0,  0,
    // -X face (back)
    -0.5, -0.5,  0.5, -1,  0,  0,  -0.5,  0.5,  0.5, -1,  0,  0,  -0.5,  0.5, -0.5, -1,  0,  0,
    -0.5, -0.5,  0.5, -1,  0,  0,  -0.5,  0.5, -0.5, -1,  0,  0,  -0.5, -0.5, -0.5, -1,  0,  0,
    // +Z face (right)
     0.5, -0.5,  0.5,  0,  0,  1,   0.5,  0.5,  0.5,  0,  0,  1,  -0.5,  0.5,  0.5,  0,  0,  1,
     0.5, -0.5,  0.5,  0,  0,  1,  -0.5,  0.5,  0.5,  0,  0,  1,  -0.5, -0.5,  0.5,  0,  0,  1,
    // -Z face (left)
    -0.5, -0.5, -0.5,  0,  0, -1,  -0.5,  0.5, -0.5,  0,  0, -1,   0.5,  0.5, -0.5,  0,  0, -1,
    -0.5, -0.5, -0.5,  0,  0, -1,   0.5,  0.5, -0.5,  0,  0, -1,   0.5, -0.5, -0.5,  0,  0, -1,
  ]);
}

/** Build a unit cylinder (radius 0.5, length 1.0) along the Z axis. */
function buildCylinder(segments: number): Float32Array {
  const data: number[] = [];
  const r = 0.5;
  const halfLen = 0.5;

  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    const x0 = Math.cos(a0) * r, y0 = Math.sin(a0) * r;
    const x1 = Math.cos(a1) * r, y1 = Math.sin(a1) * r;
    const nx0 = Math.cos(a0), ny0 = Math.sin(a0);
    const nx1 = Math.cos(a1), ny1 = Math.sin(a1);

    // Side face: two triangles per segment
    data.push(x0, y0, -halfLen, nx0, ny0, 0);
    data.push(x1, y1, -halfLen, nx1, ny1, 0);
    data.push(x1, y1, halfLen, nx1, ny1, 0);
    data.push(x0, y0, -halfLen, nx0, ny0, 0);
    data.push(x1, y1, halfLen, nx1, ny1, 0);
    data.push(x0, y0, halfLen, nx0, ny0, 0);

    // Front cap (+Z)
    data.push(0, 0, halfLen, 0, 0, 1);
    data.push(x0, y0, halfLen, 0, 0, 1);
    data.push(x1, y1, halfLen, 0, 0, 1);

    // Back cap (-Z)
    data.push(0, 0, -halfLen, 0, 0, -1);
    data.push(x1, y1, -halfLen, 0, 0, -1);
    data.push(x0, y0, -halfLen, 0, 0, -1);
  }

  return new Float32Array(data);
}

// --- Post-processing ---

/** Lazily build the post-process pipeline (idempotent). Called once after the
 *  first frame has painted, so the shader compiles + FBO allocations don't sit
 *  on the critical path to first paint. */
export function ensurePostProcess(r: Renderer): void {
  if (r.pp) return;
  r.pp = initPostProcess(r.gl, r.skyQuad.vao, r.canvas.width, r.canvas.height);
}

function initPostProcess(
  gl: WebGL2RenderingContext,
  skyQuadVao: WebGLVertexArrayObject,
  w: number,
  h: number
): PostProcess {
  const hw = Math.max(1, w >> 1);
  const hh = Math.max(1, h >> 1);

  function makeTex(tw: number, th: number): WebGLTexture {
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, tw, th, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }

  function makeColorFbo(tex: WebGLTexture): WebGLFramebuffer {
    const fbo = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return fbo;
  }

  // Scene target: full-res colour + depth renderbuffer
  const sceneTex = makeTex(w, h);
  const sceneDepth = gl.createRenderbuffer()!;
  gl.bindRenderbuffer(gl.RENDERBUFFER, sceneDepth);
  gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
  gl.bindRenderbuffer(gl.RENDERBUFFER, null);
  const sceneFbo = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, sceneFbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, sceneTex, 0);
  gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, sceneDepth);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  // Bloom ping-pong FBOs at half resolution
  const bloomTexA = makeTex(hw, hh);
  const bloomFboA = makeColorFbo(bloomTexA);
  const bloomTexB = makeTex(hw, hh);
  const bloomFboB = makeColorFbo(bloomTexB);

  // Programs — reuse sky.vert for the fullscreen quad vertex stage
  const extractProg = createProgram(gl, skyVertSrc, bloomExtractFragSrc);
  const blurProg    = createProgram(gl, skyVertSrc, bloomBlurFragSrc);
  const compositeProg = createProgram(gl, skyVertSrc, compositeFragSrc);

  function ul(prog: WebGLProgram, name: string): WebGLUniformLocation {
    return gl.getUniformLocation(prog, name)!;
  }

  return {
    sceneFbo, sceneTex, sceneDepth,
    bloomFboA, bloomTexA,
    bloomFboB, bloomTexB,
    quad: skyQuadVao,
    extractProg,
    uExtractScene: ul(extractProg, "uScene"),
    blurProg,
    uBlurInput: ul(blurProg, "uBlurInput"),
    uBlurTexel: ul(blurProg, "uTexelSize"),
    uBlurHoriz: ul(blurProg, "uHorizontal"),
    compositeProg,
    uCompScene:     ul(compositeProg, "uScene"),
    uCompBloom:     ul(compositeProg, "uBloom"),
    uCompBloomStr:  ul(compositeProg, "uBloomStrength"),
    uCompMotionBlur: ul(compositeProg, "uMotionBlur"),
    w, h,
  };
}

/** Run the bloom + motion-blur post-process pipeline and composite to canvas.
 *  Must be called after the scene (including particles) has been rendered to pp.sceneFbo. */
export function runPostProcess(r: Renderer, speedFrac: number): void {
  const { gl, pp } = r;
  if (!pp) return; // not built yet (lazy init pending)
  const hw = Math.max(1, pp.w >> 1);
  const hh = Math.max(1, pp.h >> 1);

  const quadVao = pp.quad;
  function drawQuad(): void {
    gl.bindVertexArray(quadVao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
  }

  // 1. Bloom extract (full → half): threshold bright pixels into bloomTexA
  gl.bindFramebuffer(gl.FRAMEBUFFER, pp.bloomFboA);
  gl.viewport(0, 0, hw, hh);
  gl.useProgram(pp.extractProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pp.sceneTex);
  gl.uniform1i(pp.uExtractScene, 0);
  drawQuad();

  // 2. Gaussian blur — two iterations of H+V for wider radius
  for (let pass = 0; pass < 2; pass++) {
    // Horizontal: bloomTexA → bloomFboB
    gl.bindFramebuffer(gl.FRAMEBUFFER, pp.bloomFboB);
    gl.useProgram(pp.blurProg);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, pp.bloomTexA);
    gl.uniform1i(pp.uBlurInput, 0);
    gl.uniform2f(pp.uBlurTexel, 1.0 / hw, 1.0 / hh);
    gl.uniform1i(pp.uBlurHoriz, 1);
    drawQuad();

    // Vertical: bloomTexB → bloomFboA
    gl.bindFramebuffer(gl.FRAMEBUFFER, pp.bloomFboA);
    gl.bindTexture(gl.TEXTURE_2D, pp.bloomTexB);
    gl.uniform1i(pp.uBlurHoriz, 0);
    drawQuad();
  }

  // 3. Composite to canvas
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, pp.w, pp.h);
  gl.useProgram(pp.compositeProg);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pp.sceneTex);
  gl.uniform1i(pp.uCompScene, 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, pp.bloomTexA);
  gl.uniform1i(pp.uCompBloom, 1);
  gl.uniform1f(pp.uCompBloomStr, 0.35);
  // Motion blur: 0 at rest, ~0.025 at 300 km/h
  gl.uniform1f(pp.uCompMotionBlur, speedFrac * 0.025);
  drawQuad();
}

// --- Mesh creation ---

function createMesh(
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  data: Float32Array,
  mode: number
): StaticMesh {
  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);

  const vbo = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);

  const aPos = gl.getAttribLocation(program, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 24, 0);

  const aNormal = gl.getAttribLocation(program, "aNormal");
  gl.enableVertexAttribArray(aNormal);
  gl.vertexAttribPointer(aNormal, 3, gl.FLOAT, false, 24, 12);

  gl.bindVertexArray(null);

  return { vao, count: data.length / 6, mode };
}

// --- Shader helpers ---

function createShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string
): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${info}`);
  }
  return shader;
}

function createProgram(
  gl: WebGL2RenderingContext,
  vs: string,
  fs: string
): WebGLProgram {
  const vert = createShader(gl, gl.VERTEX_SHADER, vs);
  const frag = createShader(gl, gl.FRAGMENT_SHADER, fs);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vert);
  gl.attachShader(prog, frag);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(prog);
    throw new Error(`Program link error: ${info}`);
  }
  return prog;
}

// --- Shader HMR ---

let activeRenderer: Renderer | null = null;

export function setActiveRenderer(r: Renderer): void {
  activeRenderer = r;
}

function hotReloadShaders(newVert: string, newFrag: string): void {
  if (!activeRenderer) return;
  const { gl } = activeRenderer;
  try {
    // Bind attribute locations to match existing VAOs
    const vert = createShader(gl, gl.VERTEX_SHADER, newVert);
    const frag = createShader(gl, gl.FRAGMENT_SHADER, newFrag);
    const program = gl.createProgram()!;
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    const oldProg = activeRenderer.program;
    gl.bindAttribLocation(program, gl.getAttribLocation(oldProg, "aPos"), "aPos");
    gl.bindAttribLocation(program, gl.getAttribLocation(oldProg, "aNormal"), "aNormal");
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const info = gl.getProgramInfoLog(program);
      throw new Error(`Program link error: ${info}`);
    }
    gl.deleteProgram(oldProg);
    activeRenderer.program = program;
    gl.useProgram(program);
    activeRenderer.u = {
      model: gl.getUniformLocation(program, "uModel")!,
      view: gl.getUniformLocation(program, "uView")!,
      projection: gl.getUniformLocation(program, "uProjection")!,
      color: gl.getUniformLocation(program, "uColor")!,
      lightDir: gl.getUniformLocation(program, "uLightDir")!,
      ambient: gl.getUniformLocation(program, "uAmbient")!,
      roughness: gl.getUniformLocation(program, "uRoughness")!,
      metallic: gl.getUniformLocation(program, "uMetallic")!,
      cameraPos: gl.getUniformLocation(program, "uCameraPos")!,
      shadowMatrix: gl.getUniformLocation(program, "uShadowMatrix")!,
      shadowMap: gl.getUniformLocation(program, "uShadowMap")!,
    };
    // Re-set lighting uniforms
    const lx = 0.3, ly = 0.9, lz = 0.2;
    const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
    gl.uniform3f(activeRenderer.u.lightDir, lx / lLen, ly / lLen, lz / lLen);
    gl.uniform3f(activeRenderer.u.ambient, 0.3, 0.3, 0.35);
    console.log("[HMR] Shaders recompiled");
  } catch (e) {
    console.error("[HMR] Shader recompilation failed:", e);
  }
}

if (import.meta.hot) {
  import.meta.hot.accept("./shaders/main.vert", (mod) => {
    if (mod) hotReloadShaders(mod.default, fragSrc);
  });
  import.meta.hot.accept("./shaders/main.frag", (mod) => {
    if (mod) hotReloadShaders(vertSrc, mod.default);
  });
  import.meta.hot.accept("./shaders/sky.vert", (mod) => {
    if (mod && activeRenderer) {
      try {
        const prog = createProgram(activeRenderer.gl, mod.default, skyFragSrc);
        activeRenderer.gl.deleteProgram(activeRenderer.skyProgram);
        activeRenderer.skyProgram = prog;
        console.log("[HMR] Sky shaders recompiled");
      } catch (e) { console.error("[HMR] Sky shader failed:", e); }
    }
  });
  import.meta.hot.accept("./shaders/sky.frag", (mod) => {
    if (mod && activeRenderer) {
      try {
        const prog = createProgram(activeRenderer.gl, skyVertSrc, mod.default);
        activeRenderer.gl.deleteProgram(activeRenderer.skyProgram);
        activeRenderer.skyProgram = prog;
        console.log("[HMR] Sky shaders recompiled");
      } catch (e) { console.error("[HMR] Sky shader failed:", e); }
    }
  });
}
